import React from 'react';

interface DateSelectorProps {
  dates: string[];
  selectedDate: string;
  onDateSelect: (date: string) => void;
}

export const DateSelector: React.FC<DateSelectorProps> = ({
  dates,
  selectedDate,
  onDateSelect
}) => {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      day: date.getDate(),
      year: date.getFullYear()
    };
  };

  if (dates.length <= 1) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">Select Test Date</h3>
        <span className="text-xs text-gray-500">{dates.length} dates available</span>
      </div>
      
      {/* Date tabs for 2-5 dates */}
      {dates.length <= 5 ? (
        <div className="flex gap-2">
          {dates.map((date) => {
            const { month, day, year } = formatDate(date);
            const isSelected = date === selectedDate;
            
            return (
              <button
                key={date}
                onClick={() => onDateSelect(date)}
                className={`
                  flex-1 p-3 rounded-lg border-2 transition-all
                  ${isSelected 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
              >
                <div className="text-center">
                  <div className={`text-xs font-medium ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}>
                    {month}
                  </div>
                  <div className={`text-2xl font-bold ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                    {day}
                  </div>
                  <div className={`text-xs ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}>
                    {year}
                  </div>
                </div>
                {isSelected && (
                  <div className="mt-2 text-xs font-medium text-blue-600">
                    Currently Viewing
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        /* Dropdown for more than 5 dates */
        <div className="relative">
          <select
            value={selectedDate}
            onChange={(e) => onDateSelect(e.target.value)}
            className="w-full p-3 pr-10 border-2 border-gray-200 rounded-lg appearance-none focus:border-blue-500 focus:outline-none bg-white"
          >
            {dates.map((date) => {
              const { month, day, year } = formatDate(date);
              return (
                <option key={date} value={date}>
                  {`${month} ${day}, ${year}`}
                </option>
              );
            })}
          </select>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      )}

      {/* Navigation hint */}
      <div className="mt-3 flex items-center justify-center text-xs text-gray-500">
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Click any date above to view results from that day
      </div>
    </div>
  );
};